import React from 'react';
import { Table, TablePaginationConfig } from 'antd';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { TTBStatus, TTBTableDataType, TTBType } from '../../../types/tryThenBuy';
import { TRY_THEN_BUY } from '../../../constants/define/translation';
import {
  ActionsColumn,
  AmountTotalColumn,
  CommonColumn,
  DateColumn,
  EmailColumn,
  IndexColumn,
  ProductNameColumn,
  ShipmentColumn,
  StatusColumn,
} from './columns';
import { FORMAT_DATE_US } from '../../../constants/common';
import { getDetectionHighlightTTB } from '../../../utils/transfer/ttb';
import { appStyles } from '../../../styles';
import PauseColumn from './columns/PauseColumn';

interface TTBTableProps {
  records: TTBTableDataType[];
  loading?: boolean;
  className?: string;
  onRefresh: () => void;
  getFilteredData?: (data: any) => void;
  pagination?: TablePaginationConfig;
  isRoleTTBAgent?: boolean;
  isRoleAdmin?: boolean;
  isRoleTTBManager?: boolean;
  isSearching?: boolean;
}

const { Column } = Table;
export const TTBTable = (props: TTBTableProps) => {
  const { t } = useTranslation(TRY_THEN_BUY);
  const {
    onRefresh,
    records,
    pagination,
    isRoleTTBAgent,
    isRoleAdmin,
    isRoleTTBManager,
    getFilteredData,
    isSearching,
  } = props;
  return (
    <>
      <StyledTable
        dataSource={records}
        loading={props.loading}
        className={props.className}
        rowKey="index"
        pagination={pagination}
        scroll={{ x: 1440 }}
        locale={{ emptyText: isSearching ? t('message.orderNotFound') : undefined }}
      >
        <Column
          title={t('table.title.index')}
          align="center"
          width={32}
          dataIndex="index"
          key="index"
          render={(value, record: TTBTableDataType) => (
            <IndexColumn value={value} className={`isHighLightTTB-${getDetectionHighlightTTB(record)}`} />
          )}
        />
        <Column
          title={t('table.title.orderId')}
          align="left"
          width={130}
          dataIndex="orderId"
          key="orderId"
          render={(value, record: TTBTableDataType) => (
            <CommonColumn value={value} className={`isHighLightTTB-${getDetectionHighlightTTB(record)}`} />
          )}
        />
        {!isRoleTTBAgent && (
          <Column
            title={t('table.title.email')}
            align="left"
            width={170}
            key="email"
            dataIndex="email"
            render={(value, record: TTBTableDataType) => (
              <EmailColumn email={value} className={`isHighLightTTB-${getDetectionHighlightTTB(record)}`} width={170} />
            )}
          />
        )}
        <Column
          title={t('table.title.productName')}
          align="left"
          dataIndex="productName"
          key="productName"
          render={(value, record: TTBTableDataType) => (
            <ProductNameColumn record={record} className={`isHighLightTTB-${getDetectionHighlightTTB(record)}`} />
          )}
          width={150}
        />
        <Column
          title={t('table.title.price')}
          align="center"
          width={50}
          dataIndex="amountTotal"
          key="amountTotal"
          render={(value, record: TTBTableDataType) => (
            <AmountTotalColumn total={value} className={`isHighLightTTB-${getDetectionHighlightTTB(record)}`} />
          )}
        />
        {!isRoleTTBAgent && (
          <>
            <Column
              title={t('table.title.paid')}
              align="center"
              width={50}
              dataIndex="amountPaid"
              key="amountPaid"
              render={(value, record: TTBTableDataType) => (
                <AmountTotalColumn total={value} className={`isHighLightTTB-${getDetectionHighlightTTB(record)}`} />
              )}
            />
            <Column
              title={t('table.title.orderDate')}
              align="center"
              width={50}
              dataIndex="createdAt"
              key="createdAt"
              render={(value, record: TTBTableDataType) => (
                <DateColumn date={value} className={`isHighLightTTB-${getDetectionHighlightTTB(record)}`} />
              )}
            />
            <Column
              title={t('table.title.scheduledArrivalDate')}
              align="center"
              width={50}
              dataIndex="scheduledArrivalDate"
              key="scheduledArrivalDate"
              render={(value, record: TTBTableDataType) => (
                <DateColumn
                  date={value}
                  isHighlight={record?.status === TTBStatus.TRIAL_COMING && !!value}
                  className={`isHighLightTTB-${getDetectionHighlightTTB(record)}`}
                />
              )}
            />
            <Column
              title={t('table.title.shipmentDate')}
              align="center"
              width={50}
              dataIndex="shippedAt"
              key="shippedAt"
              render={(value, record: TTBTableDataType) => (
                <DateColumn date={value} className={`isHighLightTTB-${getDetectionHighlightTTB(record)}`} />
              )}
            />
            <Column
              title={t('table.title.deliveredAt')}
              align="center"
              width={50}
              dataIndex="deliveredAt"
              key="deliveredAt"
              render={(value, record: TTBTableDataType) => (
                <DateColumn
                  date={value}
                  format={FORMAT_DATE_US}
                  className={`isHighLightTTB-${getDetectionHighlightTTB(record)}`}
                />
              )}
            />
            <Column
              title={t('table.title.startedAt')}
              align="center"
              width={50}
              dataIndex="startedAt"
              key="startedAt"
              render={(value, record: TTBTableDataType) => (
                <DateColumn date={value} className={`isHighLightTTB-${getDetectionHighlightTTB(record)}`} />
              )}
            />
          </>
        )}
        <Column
          title={t('table.title.shipmentTrackingUrl')}
          align="center"
          width={80}
          dataIndex="shipmentTrackingNumber"
          key="shipmentTrackingNumber"
          render={(_, record: TTBTableDataType) => <ShipmentColumn record={record} />}
        />
        <Column
          title={t('table.title.status')}
          align="center"
          width={80}
          dataIndex="status"
          key="status"
          render={(_, record: any) => (
            <StatusColumn
              status={record.status}
              isActive={record.status && [TTBStatus.BOUGHT, TTBStatus.CHARGED].includes(record.status)}
              width="160px"
              record={record}
            />
          )}
        />
        {isRoleTTBAgent ? (
          <Column
            title={t('table.title.action')}
            align="left"
            key="action"
            width={60}
            render={(_, record: TTBType) => (
              <ActionsColumn
                onRefresh={onRefresh}
                record={record}
                isRoleTTBManager={isRoleTTBManager}
                isRoleTTBAgent={isRoleTTBAgent}
                getFilteredData={getFilteredData}
              />
            )}
          />
        ) : (
          <>
            {(isRoleAdmin || isRoleTTBManager) && (
              <Column
                title={t('table.title.pause')}
                align="center"
                key="pause"
                width={60}
                render={(_, record: TTBType) => <PauseColumn onRefresh={onRefresh} record={record} />}
              />
            )}
            {(isRoleAdmin || isRoleTTBManager) && (
              <Column
                title={t('table.title.action')}
                align="left"
                key="action"
                width={60}
                render={(_, record: TTBType) => (
                  <ActionsColumn
                    onRefresh={onRefresh}
                    record={record}
                    isRoleTTBManager={isRoleTTBManager}
                    isRoleAdmin={isRoleAdmin}
                    getFilteredData={getFilteredData}
                  />
                )}
              />
            )}
          </>
        )}
      </StyledTable>
    </>
  );
};

const StyledTable = styled(Table)`
  .ant-table-thead {
    border-bottom: 2px solid #eee;
    & > th > tr {
      ${appStyles.background.tableBgGray}
      text-transform: uppercase;
      font-size: 0.75rem;
      font-weight: 600;
      border: 1px solid #dededee3;
      &:before {
        display: none;
      }
    }
  }

  .ant-pagination-item a {
    line-height: 30px;
  }

  .card-name-tiles {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    line-height: 21px;
    max-height: 48px;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  & th.action-home {
    text-align: center !important;
  }

  & .isHighLightTTB-red {
    ${appStyles.color.red}
    & span {
      ${appStyles.color.red}
    }
  }

  & .isHighLightTTB-orange {
    ${appStyles.color.orange}
    & span {
      ${appStyles.color.orange}
    }
  }

  & .isHighLightTTB-original {
    ${appStyles.color.black}
  }
`;
